import { Link } from '@tanstack/react-router';
import { Button } from '../../../components/ui/Button';
import { cn } from '@repo/utils';
import type { ReportDefinitionListItem, ReportFormat } from '../types';

const FORMAT_LABELS: Record<ReportFormat, string> = {
 PDF: 'PDF',
 XLSX: 'XLSX',
 CSV: 'CSV',
};

const FORMAT_COLORS: Record<ReportFormat, string> = {
 PDF: 'bg-danger-bg/40 text-danger-text',
 XLSX: 'bg-success-bg/40 text-success-text',
 CSV: 'bg-info-bg/40 text-info-text',
};

interface ReportCardProps {
 definition: ReportDefinitionListItem;
 onRun?: (definition: ReportDefinitionListItem) => void;
 onSchedule?: (definition: ReportDefinitionListItem) => void;
}

export function ReportCard({ definition, onRun, onSchedule }: ReportCardProps) {
 return (
 <div className="bg-surface-1 border border-border rounded-[12px] p-4 flex flex-col gap-3 hover:border-accent-500/50 transition-colors">
 <div className="flex flex-col gap-1 flex-1">
 <h3 className="font-semibold text-text-primary text-heading-sm leading-snug">
 {definition.displayName}
 </h3>
 {definition.description && (
 <p className="text-xs text-text-secondary line-clamp-2">
 {definition.description}
 </p>
 )}
 </div>

 <div className="flex items-center gap-1.5 flex-wrap">
 {definition.viewable && (
 <span className="inline-flex items-center px-1.5 py-0.5 rounded text-caption font-medium tracking-wide bg-accent-500/10 text-accent-500">
 In-app
 </span>
 )}
 {definition.allowedFormats.map((fmt) => (
 <span
 key={fmt}
 className={cn(
 'inline-flex items-center px-1.5 py-0.5 rounded text-caption font-medium tracking-wide',
 FORMAT_COLORS[fmt],
 )}
 >
 {FORMAT_LABELS[fmt]}
 </span>
 ))}
 </div>

 <div className="flex items-center gap-2 pt-1 border-t border-border">
 {definition.viewable ? (
 <>
 <Link
 to="/reports/$code"
 params={{ code: definition.reportCode }}
 className="flex-1"
 >
 <Button
 variant="primary"
 size="sm"
 className="w-full"
 >
 View
 </Button>
 </Link>
 {onRun && definition.allowedFormats.length > 0 && (
 <Button
 variant="secondary"
 size="sm"
 onClick={() => onRun(definition)}
 >
 Export
 </Button>
 )}
 </>
 ) : (
 onRun && (
 <Button
 variant="primary"
 size="sm"
 onClick={() => onRun(definition)}
 className="flex-1"
 >
 Run
 </Button>
 )
 )}
 {onSchedule && (
 <Button
 variant="ghost"
 onClick={() => onSchedule(definition)}
 className={cn(
 'h-7 w-7 flex items-center justify-center rounded-[6px] p-0 bg-transparent hover:bg-transparent',
 'border border-border text-text-secondary',
 'hover:border-accent-500 hover:text-accent-500 transition-colors',
 'text-sm font-medium leading-none',
 )}
 title="Schedule this report"
 aria-label={`Schedule ${definition.displayName}`}
 >
 +
 </Button>
 )}
 </div>
 </div>
 );
}
